import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box } from '@mui/material';

/**
 * Dependency-free resizable splitter (WAI-ARIA window splitter pattern).
 *
 * `SplitHandle` is the separator itself. While dragging it writes the new size into a CSS
 * custom property on `targetRef` inside requestAnimationFrame (no React state per move), then
 * commits once on pointer-up through `onCommit`. Keyboard: arrows step 16px (Shift: 64px),
 * Home/End jump to min/max, Enter or double-click resets to the default.
 *
 * `SplitPane` is a ready-made two-pane layout built on it, with an optional persisted size.
 */

const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;

/**
 * Clamp into [min, max].
 * @param {number} value - Value.
 * @param {number} min - Min.
 * @param {number} max - Max.
 * @returns {number} Clamped, rounded value.
 */
const clamp = (value, min, max) => Math.round(Math.min(Math.max(value, min), Math.max(min, max)));

/**
 * New size after a keyboard command, or null when the key is not handled.
 * @param {{key: string, shiftKey?: boolean}} event - Key event (or a plain object in tests).
 * @param {{axis: 'x'|'y', sign: 1|-1, size: number, min: number, max: number, defaultSize: number}} config - Splitter config.
 * @returns {number|null} Next size.
 */
export function sizeForKey(event, { axis, sign, size, min, max, defaultSize }) {
  const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
  const forward = axis === 'x' ? 'ArrowRight' : 'ArrowDown';
  const backward = axis === 'x' ? 'ArrowLeft' : 'ArrowUp';
  switch (event.key) {
    case forward:
      return clamp(size + step * sign, min, max);
    case backward:
      return clamp(size - step * sign, min, max);
    case 'Home':
      return min;
    case 'End':
      return max;
    case 'Enter':
      return clamp(defaultSize, min, max);
    default:
      return null;
  }
}

/**
 * The separator element.
 * @param {object} props - Props.
 * @param {'x'|'y'} props.axis - 'x' resizes a width (vertical bar), 'y' a height (horizontal bar).
 * @param {1|-1} props.sign - +1 when moving right/down grows the pane, -1 when it shrinks it.
 * @param {number} props.size - Committed size (px).
 * @param {number} props.min - Min size.
 * @param {number} props.max - Max size.
 * @param {number} props.defaultSize - Size restored by double-click / Enter.
 * @param {(size: number) => void} props.onCommit - Called once per drag (pointer-up) or key press.
 * @param {React.RefObject<HTMLElement>} [props.targetRef] - Element that receives the live CSS variable while dragging.
 * @param {string} [props.cssVar] - Variable name written on targetRef (e.g. '--q-dock-size').
 * @param {string} props.label - Accessible name, e.g. "Resize Inky panel".
 * @param {string} [props.controls] - id of the pane being resized.
 * @param {boolean} [props.disabled] - Hide from interaction.
 * @param {object} [props.sx] - Extra styles.
 * @returns {JSX.Element} Separator.
 */
export function SplitHandle({ axis = 'x', sign = 1, size, min, max, defaultSize, onCommit, targetRef, cssVar, label, controls, disabled = false, sx }) {
  const handleRef = useRef(null);
  const dragRef = useRef(null);
  const frameRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const limitsRef = useRef({ min, max });
  limitsRef.current = { min, max };
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  const writeLive = useCallback((value) => {
    const el = targetRef?.current;
    if (el && cssVar) el.style.setProperty(cssVar, `${value}px`);
    handleRef.current?.setAttribute('aria-valuenow', String(value));
  }, [targetRef, cssVar]);

  const endDrag = useCallback((commit) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    cancelAnimationFrame(frameRef.current);
    document.body.style.removeProperty('cursor');
    document.body.style.removeProperty('user-select');
    targetRef?.current?.removeAttribute('data-resizing');
    setDragging(false);
    try {
      handleRef.current?.releasePointerCapture?.(drag.pointerId);
    } catch {
      /* capture already released */
    }
    if (commit && drag.current !== drag.start) commitRef.current?.(drag.current);
    else writeLive(drag.start);
  }, [targetRef, writeLive]);

  const onPointerDown = (event) => {
    if (disabled || event.button !== 0) return;
    event.preventDefault();
    const pos = axis === 'x' ? event.clientX : event.clientY;
    dragRef.current = { pointerId: event.pointerId, startPos: pos, start: size, current: size };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      /* jsdom / old browsers */
    }
    document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    targetRef?.current?.setAttribute('data-resizing', 'true');
    setDragging(true);
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const pos = axis === 'x' ? event.clientX : event.clientY;
    if (!Number.isFinite(pos)) return;
    const { min: lo, max: hi } = limitsRef.current;
    drag.current = clamp(drag.start + (pos - drag.startPos) * sign, lo, hi);
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => writeLive(drag.current));
  };

  const onKeyDown = (event) => {
    if (disabled) return;
    const next = sizeForKey(event, { axis, sign, size, min, max, defaultSize });
    if (next === null) {
      if (event.key === 'Escape' && dragRef.current) {
        event.preventDefault();
        endDrag(false);
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (next !== size) onCommit(next);
  };

  const onDoubleClick = () => {
    if (disabled) return;
    const next = clamp(defaultSize, min, max);
    if (next !== size) onCommit(next);
  };

  // Committed size wins over any live value left from a drag.
  useEffect(() => {
    if (!dragRef.current) writeLive(clamp(size, min, max));
  }, [size, min, max, writeLive]);

  useEffect(() => () => endDrag(false), [endDrag]);

  const isX = axis === 'x';
  return (
    <Box
      ref={handleRef}
      role="separator"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      aria-controls={controls}
      aria-orientation={isX ? 'vertical' : 'horizontal'}
      aria-valuenow={clamp(size, min, max)}
      aria-valuemin={min}
      aria-valuemax={max}
      data-dragging={dragging ? 'true' : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => endDrag(true)}
      onPointerCancel={() => endDrag(false)}
      onLostPointerCapture={() => endDrag(true)}
      onKeyDown={onKeyDown}
      onDoubleClick={onDoubleClick}
      sx={{
        position: 'relative',
        flex: 'none',
        zIndex: 2,
        touchAction: 'none',
        outline: 'none',
        cursor: isX ? 'col-resize' : 'row-resize',
        ...(isX ? { width: 6, mx: '-3px', alignSelf: 'stretch' } : { height: 6, my: '-3px', width: '100%' }),
        '&::after': {
          content: '""',
          position: 'absolute',
          ...(isX ? { top: 0, bottom: 0, left: '50%', width: 2, transform: 'translateX(-50%)' } : { left: 0, right: 0, top: '50%', height: 2, transform: 'translateY(-50%)' }),
          borderRadius: 2,
          backgroundColor: 'transparent',
          transition: 'background-color 120ms ease',
        },
        '&:hover::after, &[data-dragging="true"]::after': { backgroundColor: 'var(--q-accent-border, #DDD7F7)' },
        '&:focus-visible::after': { backgroundColor: 'var(--q-accent, #4F3BD6)' },
        ...(disabled ? { pointerEvents: 'none' } : {}),
        ...sx,
      }}
    />
  );
}

/**
 * Read a persisted pane size.
 * @param {string|undefined} storageKey - localStorage key.
 * @returns {number|null} Size or null.
 */
const readSize = (storageKey) => {
  if (!storageKey) return null;
  try {
    const value = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

/**
 * Two-pane layout with a resizable divider. The `resizable` pane gets the explicit size; the
 * other one takes the rest.
 * @param {object} props - Props.
 * @param {'horizontal'|'vertical'} [props.direction='horizontal'] - Panes side by side or stacked.
 * @param {'start'|'end'} [props.resizable='end'] - Which pane the size applies to.
 * @param {number} [props.defaultSize=320] - Initial / reset size (px).
 * @param {number} [props.minSize=200] - Min size.
 * @param {number} [props.maxSize=720] - Max size.
 * @param {string} [props.storageKey] - Persist the size under this localStorage key.
 * @param {(size: number) => void} [props.onSizeChange] - Called on commit.
 * @param {boolean} [props.collapsed] - Hide the resizable pane (and the divider).
 * @param {string} [props.label='Resize panel'] - Divider accessible name.
 * @param {[React.ReactNode, React.ReactNode]} props.children - Exactly two panes.
 * @param {object} [props.sx] - Root styles.
 * @returns {JSX.Element} Split layout.
 */
export function SplitPane({
  direction = 'horizontal',
  resizable = 'end',
  defaultSize = 320,
  minSize = 200,
  maxSize = 720,
  storageKey,
  onSizeChange,
  collapsed = false,
  label = 'Resize panel',
  children,
  sx,
}) {
  const rootRef = useRef(null);
  const paneId = `q-split-${useId().replace(/:/g, "")}`;
  const [size, setSize] = useState(() => clamp(readSize(storageKey) ?? defaultSize, minSize, maxSize));
  const [first, second] = React.Children.toArray(children);
  const isRow = direction === 'horizontal';
  const sizedFirst = resizable === 'start';

  const commit = useCallback((next) => {
    setSize(next);
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        /* best-effort */
      }
    }
    onSizeChange?.(next);
  }, [storageKey, onSizeChange]);

  const sizedSx = {
    flex: 'none',
    minWidth: 0,
    minHeight: 0,
    overflow: 'auto',
    ...(isRow ? { width: 'var(--q-split-size)' } : { height: 'var(--q-split-size)' }),
  };
  const fluidSx = { flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' };

  return (
    <Box
      ref={rootRef}
      sx={{ display: 'flex', flexDirection: isRow ? 'row' : 'column', minWidth: 0, minHeight: 0, '--q-split-size': `${size}px`, ...sx }}
    >
      <Box id={sizedFirst ? paneId : undefined} sx={sizedFirst ? { ...sizedSx, display: collapsed ? 'none' : undefined } : fluidSx}>{first}</Box>
      {!collapsed && (
        <SplitHandle
          axis={isRow ? 'x' : 'y'}
          sign={sizedFirst ? 1 : -1}
          size={size}
          min={minSize}
          max={maxSize}
          defaultSize={defaultSize}
          onCommit={commit}
          targetRef={rootRef}
          cssVar="--q-split-size"
          label={label}
          controls={paneId}
        />
      )}
      <Box id={sizedFirst ? undefined : paneId} sx={sizedFirst ? fluidSx : { ...sizedSx, display: collapsed ? 'none' : undefined }}>{second}</Box>
    </Box>
  );
}

export default SplitPane;
